import {
  Box,
  Button,
  CircularProgress,
  FormControlLabel,
  Grid,
  Paper,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import type React from 'react';
import type { BillingLedger } from '../../services/billingService';
import type { PricingSettings } from '../../shared/constants/pricing';
import type { Company } from '../../types/company';
import BillingManagementPanel from '../Admin/BillingManagementPanel';

interface LedgerTabProps {
  clientId: string;
  client: Company | null;
  ledger: BillingLedger | null;

  pricingSettings: PricingSettings | null;
  isUpdatingLedger: boolean;
  topUpAmount: string;
  setTopUpAmount: (amount: string) => void;
  handleTopUp: () => void;
  handleTogglePAYG: (newVal: boolean) => void;
  handleUpdateDiscount: (newDiscount: number) => void;
  subscriptionStatus: string;
}

export const LedgerTab: React.FC<LedgerTabProps> = ({
  clientId,
  client,
  ledger,
  pricingSettings,
  isUpdatingLedger,
  topUpAmount,
  setTopUpAmount,
  handleTopUp,
  handleTogglePAYG,
  handleUpdateDiscount,
  subscriptionStatus,
}) => {
  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Credit Ledger Management
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        This ledger tracks the central credit balance and accumulated usage for this client.
      </Typography>

      <Paper variant="outlined" sx={{ p: 3, mb: 4 }}>
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Typography variant="subtitle2" color="text.secondary">
              Current Balance
            </Typography>
            <Typography variant="h4" color="primary">
              $
              {(
                (ledger?.balance || 0) * (pricingSettings?.cost_per_credit_in_base || 0)
              ).toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </Typography>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Typography variant="subtitle2" color="text.secondary">
              Accumulated Usage
            </Typography>
            <Typography variant="h4" color="error">
              $
              {(
                (ledger?.accumulated_usage || 0) * (pricingSettings?.cost_per_credit_in_base || 0)
              ).toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}{' '}
              Unbilled
            </Typography>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Typography variant="subtitle2" color="text.secondary">
              Usage Billing Status
            </Typography>
            <FormControlLabel
              control={
                <Switch
                  checked={ledger?.isPAYG || false}
                  onChange={(e) => handleTogglePAYG(e.target.checked)}
                  disabled={isUpdatingLedger}
                />
              }
              label={ledger?.isPAYG ? 'Enabled' : 'Disabled'}
              sx={{ mt: 1 }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Discount (%)
            </Typography>
            <TextField
              type="number"
              size="small"
              value={ledger?.discount || 0}
              onChange={(e) => handleUpdateDiscount(parseInt(e.target.value, 10) || 0)}
              disabled={isUpdatingLedger}
              sx={{ width: 80 }}
              inputProps={{ min: 0, max: 100 }}
            />
          </Grid>
        </Grid>
      </Paper>

      <Typography variant="subtitle1" gutterBottom>
        Manual Top-Up
      </Typography>
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
        <TextField
          label="Credits to Add"
          type="number"
          value={topUpAmount}
          onChange={(e) => setTopUpAmount(e.target.value)}
          disabled={isUpdatingLedger}
          helperText="Use negative numbers to deduct."
          size="small"
          inputProps={{ step: 'any' }}
        />
        <Button
          variant="contained"
          onClick={handleTopUp}
          disabled={isUpdatingLedger || (parseFloat(topUpAmount) || 0) === 0}
        >
          {isUpdatingLedger ? <CircularProgress size={24} /> : 'Process Transaction'}
        </Button>
      </Box>

      <Box sx={{ mt: 4 }}>
        <BillingManagementPanel
          overrideClientId={clientId}
          overrideLedger={ledger}
          overrideSubscriptionStatus={subscriptionStatus}
          overrideSubscriptionEnd={client?.subscription?.current_period_end || null}
        />
      </Box>
    </Box>
  );
};
